import { create } from 'zustand'
import { Timestamp } from 'firebase/firestore'
import { getActiveAds } from '@/services/api/ads'
import type { Ad, AdPlacement } from '@/types/ad'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

interface AdState {
  ads: Ad[]
  isLoading: boolean
  lastFetched: number | null

  // Actions
  fetchAds: () => Promise<void>
  getAdsByPlacement: (placement?: AdPlacement) => Ad[]
  invalidate: () => void
}

export const useAdStore = create<AdState>((set, get) => ({
  ads: [],
  isLoading: false,
  lastFetched: null,

  fetchAds: async () => {
    const { lastFetched, isLoading } = get()

    // Return cached if fetched within TTL
    if (lastFetched && Date.now() - lastFetched < CACHE_TTL) return

    // Prevent duplicate in-flight requests
    if (isLoading) return

    set({ isLoading: true })

    try {
      // Fetch all active ads (no placement filter — cache the full set)
      const ads = await getActiveAds()
      set({ ads, lastFetched: Date.now(), isLoading: false })
    } catch (err) {
      console.error('Failed to fetch ads:', err)
      set({ isLoading: false })
    }
  },

  getAdsByPlacement: (placement?: AdPlacement) => {
    const { ads } = get()
    if (!placement) return ads

    const now = Timestamp.now()
    return ads.filter((ad) => {
      if (ad.placement !== placement) return false
      if (ad.startDate && ad.startDate.toMillis() > now.toMillis()) return false
      if (ad.endDate && ad.endDate.toMillis() < now.toMillis()) return false
      return true
    })
  },

  invalidate: () => set({ lastFetched: null }),
}))
